import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { IMG } from "@/lib/site";
import { EASE_EXPO, VIEWPORT, reveal } from "@/lib/motion";
import { FileText } from "lucide-react";
import { WhatsAppButton } from "./WhatsAppButton";
import { TextReveal } from "@/components/ui/TextReveal";
import { TiltCard } from "@/components/ui/TiltCard";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { fetchGoogleSheetCarousel } from "@/lib/carouselService";

const cats = [
  {
    tag: "01",
    badge: "Best Seller",
    title: "Fresh Butter Cakes",
    desc: "Custom cakes, Cupcakes — made to order with rich ingredients.",
    items: ["Butter Cake", "Cup Cakes",],
    microTag: "100% Pure Vegetarian • 24h Notice",
    img: IMG.cats.butterCake,
    msg: "Hi! I'd like to inquire/order Fresh Butter Cakes.",
  },
  {
    tag: "02",
    badge: "Gift Favorite",
    title: "Cookie Tins & Gifts",
    desc: "Kraft tins tied with twine — the easiest thoughtful gift for birthdays & celebrations.",
    items: ["Assorted tins", "Festive boxes",],
    microTag: "Gift Box • 24h Notice",
    img: IMG.cats.tins,
    msg: "Hi! I'm interested in Cookie Tins & Gifts.",
  },
  {
    tag: "03",
    badge: "Kid Favorite",
    title: "Traditional Naankatai",
    desc: "Melt-in-mouth homemade Naankatai baked fresh with love and pure ghee/butter.",
    items: ["Nankhatai", "Tea-time Bakes", "Traditional Bakes"],
    microTag: "100% Pure Vegetarian • Fresh Daily • 24h Notice",
    img: IMG.cats.nankatai,
    msg: "Hi! I'd like to order Traditional Nankhatai.",
  },
  {
    tag: "04",
    badge: "Must Try",
    title: "Handcrafted Mini Cookies",
    desc: "Crispy, buttery handmade cookies of multiple flavours baked in small batches daily.",
    items: ["Butter Cookies", "Choco Chip with choco dip", "Nutty Crunch", "Multiple Flavours"],
    microTag: "100% Pure Vegetarian • Small Batches • 24h Notice",
    img: IMG.cats.cookies,
    msg: "Hi! I'd like to order Handcrafted Mini Cookies.",
  },
  {
    tag: "05",
    badge: "Family Pick",
    title: "Specialty Tea Cakes",
    desc: "Soft, fragrant tea-time slices and signature family recipes for afternoon tea.",
    items: ["Butter Cake", "Tea Slices", "Mom's Special"],
    microTag: "100% Pure Vegetarian • Fresh Baked • 24h Notice",
    img: IMG.cats.butterCake2,
    msg: "Hi! I'm interested in Specialty Tea Cakes.",
  },
  {
    tag: "06",
    badge: "Easy Bake",
    title: "Ready-to-Eat Premixes",
    desc: "Small-batch premixes for cakes and cookies — bake warm at home anytime.",
    items: ["Sambhar mix", "Idli Dosa mix", "Cake mix", "Breakfast mix and much more"],
    microTag: "Preservative Free • Instant • 24h Notice",
    img: IMG.cats.premix,
    msg: "Hi! Please share your Ready-to-Eat Premixes.",
  },
  {
    tag: "07",
    badge: "Chef Special",
    title: "Chocolate Cookie Bars",
    desc: "Rich, chewy chocolate cookie bars baked fresh with premium ingredients.",
    items: ["Cookie Bar", "Rich & Chewy", "Fresh Baked"],
    microTag: "100% Pure Vegetarian • Fresh Daily • 24h Notice",
    img: IMG.cats.cookieBar,
    msg: "Hi! I'd like to order Chocolate Cookie Bars.",
  },
  {
    tag: "08",
    badge: "New Favorite",
    title: "Fudgy Brownie Bites",
    desc: "Bite-sized, melt-in-your-mouth brownies with a rich flavour.",
    items: ["Brownie Bites", "Fudgy & Rich", "Party Favorite"],
    microTag: "100% Pure Vegetarian • Fresh Baked • 24h Notice",
    img: IMG.cats.brownieBites,
    msg: "Hi! I'd like to order Fudgy Brownie Bites.",
  },
];

const Panel = ({ c, i }) => (
  <TiltCard className="h-full">
    <motion.article
      data-testid={`category-panel-${i}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration: 0.38, delay: i * 0.05, ease: EASE_EXPO }}
      className="group relative bg-[#F2EBE0] rounded-[28px] overflow-hidden border border-[#3B2818]/10 h-full flex flex-col justify-between"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={c.img}
          alt={c.title}
          width="600"
          height="750"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transform-gpu will-change-transform [backface-visibility:hidden] transition-transform duration-300 ease-expo-fast group-hover:scale-[1.05]"
        />
        <div className="absolute top-5 left-5 font-serif italic text-2xl text-[#FAF6F0] mix-blend-difference">
          {c.tag}
        </div>
        {c.badge && (
          <div className="absolute top-5 right-5 bg-[#C06A22] text-[#FAF6F0] text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full shadow-md font-semibold">
            {c.badge}
          </div>
        )}
      </div>
      <div className="p-6 md:p-8 flex flex-col flex-1 justify-between">
        <div>
          <h3 className="font-serif text-3xl md:text-[38px] leading-tight text-[#3B2818]">
            {c.title}
          </h3>
          <p className="mt-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#C06A22]">
            {c.microTag}
          </p>
          <p className="mt-3 text-[15px] text-[#6B5343] leading-relaxed">
            {c.desc}
          </p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {c.items.map((it) => (
              <li
                key={it}
                className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-[#3B2818]/20 text-[#3B2818]/80"
              >
                {it}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-6">
          <WhatsAppButton
            variant="caramel"
            size="sm"
            label="Inquire / Order on WhatsApp"
            testId={`category-wa-cta-${i}`}
            msg={c.msg}
          />
        </div>
      </div>
    </motion.article>
  </TiltCard>
);

export default function Categories({ onOpenPdf }) {
  const [api, setApi] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [categoriesList, setCategoriesList] = useState(cats);

  useEffect(() => {
    fetchGoogleSheetCarousel(cats).then((items) => {
      if (items && items.length > 0) {
        setCategoriesList(items);
      }
    });
  }, []);

  useEffect(() => {
    if (!api || isHovered || isHeld) return;

    const timer = setInterval(() => {
      api.scrollNext();
    }, 3500);

    return () => clearInterval(timer);
  }, [api, isHovered, isHeld]);

  return (
    <section
      id="categories"
      data-testid="categories-section"
      className="relative py-16 sm:py-24 md:py-36 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      onPointerDown={() => setIsHeld(true)}
      onPointerUp={() => setIsHeld(false)}
      onPointerCancel={() => setIsHeld(false)}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="mb-10 sm:mb-14 md:mb-20 grid grid-cols-12 gap-4 md:gap-6 items-end">
          <motion.div {...reveal()} className="col-span-12 md:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#C06A22]">
              What we make
            </span>
            <TextReveal className="font-serif text-[32px] sm:text-[44px] md:text-6xl lg:text-7xl leading-[1.05] sm:leading-[0.95] tracking-tight mt-2 sm:mt-4 text-[#3B2818]">
              Little corners of our kitchen.
            </TextReveal>
          </motion.div>
          <motion.div
            {...reveal(0.08)}
            className="col-span-12 md:col-span-5 flex flex-col items-start md:items-end gap-4 sm:gap-5"
          >
            <p className="text-sm sm:text-[15px] text-[#6B5343] max-w-sm md:text-right">
              Pick a category, message us, or explore our full downloadable menu catalog.
            </p>
            {onOpenPdf && (
              <button
                onClick={onOpenPdf}
                data-testid="view-pdf-menu-btn"
                className="inline-flex items-center gap-2.5 px-5 py-2.5 sm:px-6 sm:py-3 rounded-full bg-[#3B2818] text-[#FAF6F0] hover:bg-[#C06A22] transition-all duration-300 shadow-md hover:shadow-xl text-[11px] sm:text-xs uppercase tracking-[0.18em] font-semibold group cursor-pointer active:scale-95 border border-[#FAF6F0]/20"
              >
                <FileText className="h-4 w-4 text-[#C06A22] group-hover:text-[#FAF6F0] transition-colors" />
                <span>View Full Menu (PDF)</span>
              </button>
            )}
          </motion.div>
        </div>

        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full relative"
        >
          <CarouselContent className="-ml-4 md:-ml-6 py-2">
            {categoriesList.map((c, i) => (
              <CarouselItem
                key={`${c.title}-${i}`}
                className="pl-4 md:pl-6 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                <Panel c={c} i={i} />
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="mt-8 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] text-[#3B2818]/60 font-medium">
              Drag or use arrows to explore
            </span>
            <div className="flex items-center gap-3">
              <CarouselPrevious className="static translate-y-0 h-11 w-11 rounded-full border border-[#3B2818]/20 bg-[#F2EBE0] text-[#3B2818] hover:bg-[#3B2818] hover:text-[#FAF6F0] transition-colors duration-200" />
              <CarouselNext className="static translate-y-0 h-11 w-11 rounded-full border border-[#3B2818]/20 bg-[#F2EBE0] text-[#3B2818] hover:bg-[#3B2818] hover:text-[#FAF6F0] transition-colors duration-200" />
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
}
